import { SEVERITY } from '../constants';
import { UnauthorizedError } from '../utils/custom-errors';
import {
  changeLocation,
  closeOverlay,
  closeSidebar,
  openSidebar,
  hitEscape,
  initializeSuccess,
  initializeError,
  openAppInModal,
  receiveAvatarUrl,
  reloadAppShell,
  storeMerchantId,
  appError,
  overlayError,
  sidebarError,
  updateTrialPeriod,
  notificationsAppointmentClick,
  notificationsMessageClick,
  initNotificationCounts,
  newNotificationCounts,
  notificationsAppointmentItemClick,
  notificationsAppointmentCloseClick,
  notificationsAppointmentClearAllClick,
  notificationsAppointmentFetchSuccess,
  notificationsAppointmentFetchError,
  openModal,
  closeModal,
  setOrganizationStatus,
  setContractDetails,
  initNotificationClientMessages,
  setupBrowserLocaleAndErrorState,
} from '../actions';
import type {
  NotificationCounts,
  ModalAppPayload,
  OrganizationStatus,
} from '../actions';
import { getLogoutUrl, getLoginUrl } from '../utils/url';
import { createFx } from '../store/fx-middleware';
import type { FxProcess, FxProcessContext } from '../store/fx-middleware';
import {
  hasUnauthorizedError,
  isTrialPeriodExpired,
  notificationsPopupIsOpen,
  getMerchantId,
  canToggleNewCalendar,
  isGravityCalendarMerchant,
} from '../selectors';
import { location } from '../env/browser';
import { isBrowserDeprecated } from '../utils/browser-utils';
import initI18next, { setupTranslations } from '../initializers/i18next';
import { clearSsoToken, setPreferredMerchantId } from '../initializers/sso';
import EVENT_BUS_EVENTS from '../constants/event-bus-events';
import Tracer from '../utils/tracer';
import createQueryConfig, { keys as queryKeys } from '../queries';

import type { QueryClient } from '@tanstack/react-query';
import type { Dispatch, PayloadAction } from '@reduxjs/toolkit';
import type { IEventBus, TrackMethod } from '@shore/types';
import type {
  AppShellApiClient,
  CurrentMerchantData,
  ErrorLogger,
  InitializeSuccessPayload,
} from '../shared/types';
import type { RootState } from '../reducers';
import type { DeprecatedRouter } from '../deprecated-router';

// ── Local interfaces ──────────────────────────────────────────────────────────

// Narrowed to just what these effects call — navigate/load/reloadLocation —
// not the full DeprecatedRouter surface (e.g. subscribe/destroy are the app
// bootstrap's concern, not an individual effect's).
type AppRouter = Pick<DeprecatedRouter, 'navigate' | 'load' | 'reloadLocation'>;

// Methods used by effects that are not yet in @shore/types IApiClient.
interface EffectsApiClient extends AppShellApiClient {
  notificationsV2: {
    unread(params: { merchant_id: string }): Promise<NotificationCounts>;
    deleteAllByType(
      type: string,
      params: { merchant_id: string }
    ): Promise<unknown>;
    list(params: {
      type: string;
      merchant_id: string;
    }): Promise<{ notifications: RawNotification[] }>;
    delete(id: string): Promise<unknown>;
  };
  // Narrowed to drop the `| undefined` AppShellApiClient carries: by the
  // time effects run (post-bootstrap), configureApiClient has already
  // guaranteed it's populated — see initializers/api-client.ts.
  getCurrentMerchantData(): Promise<CurrentMerchantData>;
  paymentSourceV2: {
    findAll(params: { filter: { contract: string } }): Promise<PaymentSource[]>;
  };
  organizationStatusV2: {
    find(orgId: string): Promise<OrganizationStatus>;
  };
}

// ErrorAction (from createErrorAction) lacks the index signature required by
// UnknownAction. Define a compatible standalone type for use in FxProcess params.
type ErrorFxAction = {
  [key: string]: unknown;
  type: string;
  payload: { message: string; name: string };
  meta: { error: unknown; extraTags: Record<string, unknown> };
};

// Raw shape of a notification object from the API list endpoint.
interface RawNotification {
  id: string;
  appointment_id: string;
  triggered_at: string;
  metadata: {
    state: string;
    appointment_type: string;
    group_id: string | null;
    starts_at: string;
    previous_datetime?: string;
    services: { name: string }[];
    customer: { first_name: string; last_name: string };
  };
}

interface PaymentSource {
  source_type: string;
  default_source?: boolean;
}

// Payload shape for the notificationsAppointmentItemClick action.
type NotificationItemPayload = {
  id: string;
  appointmentId: string;
  appointmentType: string;
  groupId: string;
};

interface EffectsDeps {
  apiClient: EffectsApiClient;
  router: AppRouter;
  queryClient: QueryClient;
  localStorage: Storage;
  eventBus: IEventBus;
  locationRef?: { pathname: string };
}

// ── Pure helpers ──────────────────────────────────────────────────────────────

function hashRoute(route: string): boolean {
  return route.startsWith('#');
}

const extractManifestOverrideDetailsFromUrl = (
  url: string
): { appName?: string; manifestUrl?: string } => {
  const match = String(url).match(
    /APP_MANIFEST_URL(%3A|:)([a-z\d-]+)=([^&#]+)/
  );
  if (!match) return {};

  const [, , appName, encodedManifestUrl] = match;

  return { appName, manifestUrl: decodeURIComponent(encodedManifestUrl) };
};

const saveManifestUrlOverride = (
  { appName, manifestUrl }: { appName?: string; manifestUrl?: string },
  sessionStorage: Storage
): void => {
  if (!appName || !manifestUrl) return;
  sessionStorage.setItem(`APP_MANIFEST_URL:${appName}`, manifestUrl);
};

// ── Error effects (Group A) ───────────────────────────────────────────────────

async function fallbackToBrowserLocale({
  action,
  dispatch,
}: FxProcessContext<Record<string, unknown>>) {
  const {
    meta: { error },
  } = action as ErrorFxAction;
  const browserLocale =
    navigator.language ||
    (navigator as Navigator & { userLanguage?: string }).userLanguage ||
    'en';

  await initI18next(browserLocale);

  try {
    await setupTranslations(browserLocale);
  } catch {
    await setupTranslations('zu');
  }

  dispatch(initializeError(error));
}

const processLogErrorFx: FxProcess<{ errorLogger: ErrorLogger }> = ({
  action,
  deps: { errorLogger },
}) => {
  const {
    meta: { error, extraTags = {} },
  } = action as ErrorFxAction;

  // We don't want to log UnauthorizedError anymore.
  if (error instanceof UnauthorizedError) return;

  errorLogger.log(error as Error, {}, extraTags);
};

export const logAppErrorFx = createFx(appError, processLogErrorFx);
export const logOverlayErrorFx = createFx(overlayError, processLogErrorFx);
export const logSidebarErrorFx = createFx(sidebarError, processLogErrorFx);
export const logInitializeErrorFx = createFx(
  initializeError,
  processLogErrorFx
);

export const fallbackToBrowserLocaleOnInitErrorFx = createFx(
  setupBrowserLocaleAndErrorState,
  fallbackToBrowserLocale
);

// Action not used in process — default UnknownAction is sufficient.
export const redirectToLoginPageWhenUnauthorizedFx = createFx<{
  router: AppRouter;
}>(initializeError, ({ getState, deps: { router } }) => {
  if (!hasUnauthorizedError(getState())) return;

  router.load(getLoginUrl(location.href));
});

// ── Lifecycle effects (Group A) ───────────────────────────────────────────────

export const storeAppManifestUrlOverwriteFx = createFx<
  { sessionStorage: Storage },
  PayloadAction<{ url: string }>
>(changeLocation, ({ action, deps: { sessionStorage } }) => {
  const { url } = action.payload;
  saveManifestUrlOverride(
    extractManifestOverrideDetailsFromUrl(url),
    sessionStorage
  );
});

export const closeOverlayFx = createFx<{ router: AppRouter }>(
  closeOverlay,
  ({ getState, deps: { router } }) => {
    const { currentOverlay } = getState();
    if (!currentOverlay.name) return;

    router.navigate('#');
  }
);

export const hitEscapeFx = createFx(hitEscape, ({ dispatch, getState }) => {
  const { currentOverlay } = getState();
  if (!currentOverlay.name) return;
  if (currentOverlay.isLoading) return;

  dispatch(closeOverlay());
});

export const storeMerchantIdFx = createFx<
  { localStorage: Storage },
  PayloadAction<string>
>(storeMerchantId, ({ getState, action, deps: { localStorage } }) => {
  const merchantId = action.payload;
  const { currentAccount } = getState();

  localStorage.setItem(`${currentAccount.id}:currentMerchantId`, merchantId);
});

export const reloadAppShellFx = createFx<{ router: AppRouter }>(
  reloadAppShell,
  ({ deps: { router } }) => {
    router.reloadLocation();
  }
);

export const changedLanguageFx = createFx(
  'APP(wrapper)/CHANGED_LANGUAGE',
  ({ dispatch }) => {
    dispatch(reloadAppShell());
  }
);

export const changeAppWithNewAppShellVersionAvailableFx = createFx(
  changeLocation,
  ({ previousState, getState, dispatch }) => {
    const currentState = getState();

    if (!currentState.documentHeaders.hasLastModifiedChanged) return;
    if (previousState.currentApp.name === currentState.currentApp.name) return;

    dispatch(reloadAppShell());
  }
);

type SelectLocationPayload = { id: string; postSwitchRedirectTo?: string };

export const selectLocationsFx = createFx<
  {
    apiClient: EffectsApiClient;
    router: AppRouter;
  },
  PayloadAction<SelectLocationPayload>
>(
  'APP(locations)/SELECT_LOCATION',
  async ({ dispatch, getState, action, deps: { apiClient, router } }) => {
    const { currentMerchant, currentAccount } = getState();
    const { id: merchantId, postSwitchRedirectTo } = action.payload;

    if (currentMerchant.id === merchantId) return;

    dispatch(storeMerchantId(merchantId));
    if (currentAccount?.id) {
      setPreferredMerchantId(currentAccount.id, merchantId);
    }
    // Cookie-based Core switch is best-effort (fails under SSO / 3P-cookie block).
    try {
      await apiClient.switchLocationOnCore(merchantId);
    } catch {
      // Preferred id in sessionStorage is enough for SSO boot after reload.
    }
    const onSuccessRedirectTo = `/${postSwitchRedirectTo || 'calendar'}`;
    router.navigate(onSuccessRedirectTo);
    dispatch(reloadAppShell());
  }
);

// ── Init effect (Group A) ─────────────────────────────────────────────────────

export const initializeSuccessFx = createFx<
  {
    apiClient: EffectsApiClient;
    queryClient: QueryClient;
    errorLogger: ErrorLogger;
  },
  PayloadAction<InitializeSuccessPayload>
>(
  initializeSuccess,
  async ({
    dispatch,
    action,
    deps: { apiClient, queryClient, errorLogger },
  }) => {
    const {
      currentMerchantData: { id },
      myAccountData: { email },
    } = action.payload;

    const unreadNotifications = await apiClient.notificationsV2.unread({
      merchant_id: id,
    });

    const queryConfig = createQueryConfig(queryClient, apiClient);

    queryClient.prefetchQuery(queryConfig.merchantsSearch());

    const clientMessages = isBrowserDeprecated() ? ['browser-deprecation'] : [];

    errorLogger.setContext({ email, id });
    dispatch(storeMerchantId(id));
    // TODO: apiClient.switchLocationOnCore(id);
    dispatch(receiveAvatarUrl((await apiClient.getAvatarUrl()) ?? ''));
    dispatch(initNotificationClientMessages(clientMessages));
    dispatch(initNotificationCounts(unreadNotifications));
  }
);

// Fired here rather than in action-event-mapping because that map allows only one
// event per action type, which is already used by the `identify` event — see the
// note in src/store/action-event-mapping.ts.
export const appShellLoadedFx = createFx<{ trackMethod?: TrackMethod }>(
  initializeSuccess,
  ({ getState, deps: { trackMethod } }) => {
    const state = getState();

    // Sent on every load, so it shows which calendar people keep, not just
    // that they clicked the toggle. Null when the user has no toggle.
    trackMethod?.('app_shell_loaded', {
      calendar_v2_opt_in: canToggleNewCalendar(state)
        ? state.currentAccount.calendarV2OptIn
        : null,
      uses_calendar_v2: isGravityCalendarMerchant(state),
    });
  }
);

export const appShellFailedToLoadFx = createFx<{ trackMethod?: TrackMethod }>(
  initializeError,
  ({ action, deps: { trackMethod } }) => {
    const {
      meta: { error },
    } = action as ErrorFxAction;

    // A not-logged-in visitor triggers an UnauthorizedError and is redirected to
    // login — that's a normal auth flow, not a load failure, so don't count it
    // (mirrors processLogErrorFx, which skips UnauthorizedError for logging).
    if (error instanceof UnauthorizedError) return;

    trackMethod?.('app_shell_failed_to_load');
  }
);

// ── Group B effects (notifications, nav, modal/sidebar, account) ─────────────

export const notificationMessageClickFx = createFx<
  EffectsDeps,
  PayloadAction<number>
>(
  notificationsMessageClick,
  async ({ deps: { router, apiClient }, action, getState }) => {
    const messageCount = action.payload;
    router.navigate('#/messenger/conversations');

    if (messageCount > 0) {
      const merchantId = getMerchantId(getState());

      await apiClient.notificationsV2.deleteAllByType('message', {
        merchant_id: merchantId!,
      });
    }
  }
);

const fetchAppointmentNotifications = async ({
  dispatch,
  getState,
  deps: { apiClient },
}: Pick<FxProcessContext<EffectsDeps>, 'dispatch' | 'getState' | 'deps'>) => {
  try {
    const merchantId = getMerchantId(getState());
    const { notifications } = await apiClient.notificationsV2.list({
      type: 'appointment',
      merchant_id: merchantId!,
    });

    const decodedAppointmentNotifications = notifications.map(
      (notification: RawNotification) => {
        const { first_name: firstName, last_name: lastName } =
          notification.metadata.customer;

        return {
          id: notification.id,
          appointmentId: notification.appointment_id,
          appointmentType: notification.metadata.appointment_type || '',
          groupId: notification.metadata.group_id || '',
          timestamp: notification.triggered_at,
          customerName: `${firstName} ${lastName}`,
          status: notification.metadata.state,
          requestedDate: notification.metadata.starts_at,
          previousDate: notification.metadata.previous_datetime,
          services: notification.metadata.services
            .map(({ name }: { name: string }) => name)
            .join(', '),
        };
      }
    );

    dispatch(
      notificationsAppointmentFetchSuccess(decodedAppointmentNotifications)
    );
  } catch {
    // TODO add error logging probbaly as redux middleware or so
    dispatch(notificationsAppointmentFetchError());
  }
};

export const notificationsAppointmentClickFx = createFx<EffectsDeps>(
  notificationsAppointmentClick,
  fetchAppointmentNotifications
);

export const newNotificationCountsFx = createFx<EffectsDeps>(
  newNotificationCounts,
  /* istanbul ignore next */
  ({ getState, dispatch, deps }) => {
    if (notificationsPopupIsOpen(getState())) {
      fetchAppointmentNotifications({ dispatch, getState, deps });
    }
  }
);

const markNotificationAsRead = async ({
  apiClient,
  id,
  dispatch,
}: {
  apiClient: EffectsApiClient;
  id: string;
  dispatch: Dispatch;
}) => {
  try {
    await apiClient.notificationsV2.delete(id);
  } catch {
    // TODO add error logging probably as redux middleware or so
    dispatch(notificationsAppointmentFetchError());
  }
};

export const notificationsAppointmentItemClickFx = createFx<
  EffectsDeps,
  PayloadAction<NotificationItemPayload>
>(
  notificationsAppointmentItemClick,
  async ({ dispatch, action, deps: { apiClient, router } }) => {
    const payload = action.payload;
    const redirectPath =
      payload.appointmentType === 'appointment_group' && payload.groupId
        ? `#/wrapper/courses/${payload.groupId}`
        : `#/appointment/${payload.appointmentId}`;
    router.navigate(redirectPath);
    markNotificationAsRead({ apiClient, id: payload.id, dispatch });
  }
);

/* istanbul ignore next */
export const notificationsAppointmentCloseClickFx = createFx<
  EffectsDeps,
  PayloadAction<string>
>(
  notificationsAppointmentCloseClick,
  async ({ dispatch, action, deps: { apiClient } }) => {
    markNotificationAsRead({
      apiClient,
      id: action.payload,
      dispatch,
    });
  }
);

export const notificationsAppointmentClearAllClickFx = createFx<EffectsDeps>(
  notificationsAppointmentClearAllClick,
  async ({ getState, deps: { apiClient } }) => {
    const merchantId = getMerchantId(getState());

    await apiClient.notificationsV2.deleteAllByType('appointment', {
      merchant_id: merchantId!,
    });
  }
);

export const clickCloseAppFx = createFx<EffectsDeps>(
  'APP(*)/CLICK_CLOSE',
  ({ dispatch }) => {
    dispatch(closeOverlay());
  }
);

export const clickCloseSidebarFx = createFx<EffectsDeps>(
  'APP(*)/CLICK_CLOSE_SIDEBAR',
  ({ dispatch, deps: { router } }) => {
    router.navigate('#');
    dispatch(closeSidebar());
  }
);

/* istanbul ignore next */
export const openSidebarFx = createFx<
  EffectsDeps,
  PayloadAction<{ url: string }>
>('APP(*)/OPEN_SIDEBAR', ({ action, dispatch }) => {
  const { url } = action.payload;
  dispatch(openSidebar(url));
});

export const navigateToAppFx = createFx<
  EffectsDeps,
  PayloadAction<{ url: string | undefined }>
>(
  'APP(*)/NAVIGATE_TO',
  ({ action, dispatch, deps: { eventBus, locationRef = location } }) => {
    const { url } = action.payload;

    if (url) {
      const currentPath = locationRef.pathname;
      const newLocation = (
        hashRoute(url) ? `${currentPath}${url}` : url
      ).replace('//', '/');

      dispatch(changeLocation(newLocation));
      eventBus.emit(EVENT_BUS_EVENTS.NAVIGATE, {
        payload: { route: newLocation },
      });
    }
  }
);

export const openModalFx = createFx<EffectsDeps>(
  'APP(*)/OPEN_MODAL',
  ({ dispatch }) => {
    /* istanbul ignore next */
    dispatch(openModal());
  }
);

export const closeModalFx = createFx<EffectsDeps>(
  'APP(*)/CLOSE_MODAL',
  ({ dispatch }) => {
    /* istanbul ignore next */
    dispatch(closeModal());
  }
);

const processLogoutFx = ({
  deps: { router },
}: {
  deps: Pick<EffectsDeps, 'router'>;
}) => {
  Tracer.removeUser();
  clearSsoToken();

  /* istanbul ignore next */
  router.load(getLogoutUrl());
};

export const logoutAppFx = createFx<EffectsDeps>(
  'APP(*)/LOGOUT',
  processLogoutFx
);

export const logoutFx = createFx<EffectsDeps>('CLICK_LOGOUT', processLogoutFx);

const processRedirectToContractPageWhenTrialEndsFx = ({
  getState,
  deps: { router, queryClient, locationRef = location },
}: {
  getState: () => RootState;
  deps: EffectsDeps;
}) => {
  const state = getState();
  const { currentOverlay, currentMerchant } = state;

  const hasChargebeeSubscription =
    currentMerchant.id &&
    queryClient.getQueryData(queryKeys.subscription.latest(currentMerchant.id));

  if (hasChargebeeSubscription) {
    return;
  }

  const currentRoute = locationRef.pathname;

  if (currentRoute.includes('contract') && !currentOverlay.name) return;

  if (isTrialPeriodExpired(state)) {
    router.navigate('/contract');
  }
};

export const redirectToContractPageOnChangeLocationFx = createFx<EffectsDeps>(
  changeLocation,
  processRedirectToContractPageWhenTrialEndsFx
);

export const redirectToContractPageOnInitializationFx = createFx<EffectsDeps>(
  initializeSuccess,
  processRedirectToContractPageWhenTrialEndsFx
);

export const updateUnreadNotificationsCountFx = createFx<EffectsDeps>(
  'APP(*)/READ_NOTIFICATION',
  async ({ dispatch, getState, deps: { apiClient } }) => {
    const merchantId = getMerchantId(getState());
    const unreadNotifications = await apiClient.notificationsV2.unread({
      merchant_id: merchantId!,
    });
    dispatch(newNotificationCounts(unreadNotifications));
  }
);

export const updateTrialPeriodFx = createFx<EffectsDeps, PayloadAction<string>>(
  'APP(*)/UPDATE_TRIAL_PERIOD',
  ({ action, dispatch }) => {
    dispatch(updateTrialPeriod(action.payload));
  }
);

export const updateAvatarFx = createFx<EffectsDeps, PayloadAction<string>>(
  'APP(*)/UPDATE_AVATAR',
  ({ action, dispatch }) => {
    dispatch(receiveAvatarUrl(action.payload));
  }
);

export const openAppInModalFx = createFx<
  EffectsDeps,
  PayloadAction<ModalAppPayload>
>('APP(*)/OPEN_APP_IN_MODAL', ({ dispatch, action }) => {
  dispatch(openAppInModal(action.payload));
});

export const setAccountContractDetailsFx = createFx<EffectsDeps>(
  initializeSuccess,
  async ({ dispatch, deps: { apiClient, queryClient } }) => {
    const queryConfig = createQueryConfig(queryClient, apiClient);
    const { organization } = await apiClient.getCurrentMerchantData();

    const contracts = await queryClient.fetchQuery(
      queryConfig.contractV2.findAll(organization.id)
    );

    const paymentSourceRequests = contracts.map(({ id }: { id: string }) =>
      apiClient.paymentSourceV2.findAll({
        filter: { contract: id },
      })
    );

    const unnest = (array: unknown[][]) =>
      array.reduce((acc: unknown[], cur) => [...acc, ...cur], []);

    const paymentSources = await Promise.all(paymentSourceRequests);

    const { source_type: defaultPaymentSourceType = null } =
      (
        unnest(paymentSources) as {
          default_source: boolean;
          source_type: string;
        }[]
      ).find((p) => p.default_source === true) || {};

    const contractDetails = {
      packages: [
        ...new Set<string>(
          contracts.reduce(
            (
              acc: string[],
              { packages = [] }: { packages?: { package_code: string }[] }
            ) => [...acc, ...packages.map((p) => p.package_code)],
            []
          )
        ),
      ],
      states: contracts.map((c: { state: string }) => c.state),
      paymentSources: [
        ...new Set(
          unnest(paymentSources).map(
            (s) => (s as { source_type: string }).source_type
          )
        ),
      ],
      defaultPaymentSourceType,
    };

    dispatch(setContractDetails(contractDetails));
  }
);

const loadOrganizationStatus = async ({
  dispatch,
  getState,
  deps: { apiClient },
}: Pick<FxProcessContext<EffectsDeps>, 'dispatch' | 'getState' | 'deps'>) => {
  const { currentAccount } = getState();
  const { organization, id: currentMerchantId } =
    await apiClient.getCurrentMerchantData();
  const isOwnerOrAdmin =
    currentAccount.organizationAdmin ||
    currentAccount.roles
      .filter(
        ({ merchant: { id } }: { merchant: { id: string } }) =>
          id === currentMerchantId
      )
      .reduce(
        (acc: boolean, { name }: { name: string }) =>
          acc || ['owner', 'admin'].includes(name),
        false
      );
  if (!isOwnerOrAdmin) {
    return;
  }

  const organizationStatus = await apiClient.organizationStatusV2.find(
    organization.id
  );

  dispatch(setOrganizationStatus(organizationStatus));
};

export const checkOrganizationStatusFx = createFx<EffectsDeps>(
  initializeSuccess,
  loadOrganizationStatus
);

export const reloadOrganizationStatusFx = createFx<EffectsDeps>(
  'APP(*)/RELOAD_ORGANIZATION_STATUS',
  loadOrganizationStatus
);

export const notificationSMSDisplaySeverityFx = createFx<
  EffectsDeps,
  PayloadAction<string>
>(
  'NOTIFICATIONS_SMS_DISPLAY_SEVERITY',
  ({ action, deps: { localStorage } }) => {
    const severity = action.payload;
    switch (severity) {
      case SEVERITY.NONE:
        localStorage.removeItem(SEVERITY.LABEL);
        break;
      case SEVERITY.WARNING:
      case SEVERITY.DANGER:
        localStorage.setItem(SEVERITY.LABEL, severity);
        break;
      default:
        localStorage.removeItem(SEVERITY.LABEL);
    }
  }
);
